import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Download,
  Check,
  CheckCircle2,
  ArrowUpRight,
  Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useAuth, beginGoogleAuth } from "@/lib/AuthContext";
import FinalCTA from "@/components/FinalCTA";

const STEP_INTRO = 0;
const STEP_CHECKLIST = 1;
const STEP_EMAIL = 2;
const STEP_RESULT = 3;

export default function AIReadiness() {
  const [step, setStep] = useState(STEP_INTRO);
  const [sections, setSections] = useState([]);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({ name: "", email: "", business_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");

  useEffect(() => {
    api.get("/checklist").then((r) => setSections(r.data.sections)).catch(() => {});
  }, []);

  const totalQuestions = sections.reduce((a, s) => a + s.questions.length, 0);
  const answeredCount = Object.keys(answers).length;
  const progressPct = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const toggle = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  const currentSection = sections[sectionIdx];
  const sectionComplete =
    currentSection &&
    currentSection.questions.every((q) => answers[q.id] !== undefined);
  const isLastSection = sectionIdx === sections.length - 1;

  const submit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", {
        name: lead.name,
        email: lead.email,
        business_name: lead.business_name,
        answers,
      });
      setResult(data.result);
      setPdfUrl(`${API}${data.pdf_url.replace(/^\/api/, "")}`);
      setStep(STEP_RESULT);
      trackEvent("ai_readiness_completed", {
        meta: { tier: data.result.tier, percent: String(data.result.percent) },
      });
      toast.success("Report ready!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative bg-[var(--tmg-ivory)] border-b border-[var(--tmg-line)] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 pt-16 pb-20 sm:pt-24 sm:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="tmg-overline-gold mb-6 tmg-fade-up flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> FREE · 3-MIN ASSESSMENT
            </p>
            <h1
              data-testid="readiness-headline"
              className="font-display text-[2.5rem] sm:text-5xl lg:text-[4rem] font-semibold leading-[1.04] tracking-tight tmg-fade-up-d1"
            >
              The AI &amp; Business Systems{" "}
              <span className="italic font-light text-[var(--tmg-gold-soft)]">Readiness Checklist</span>.
            </h1>
            <p className="mt-8 text-base sm:text-lg text-[var(--tmg-muted)] max-w-xl leading-relaxed tmg-fade-up-d2">
              Answer 16 quick questions and get a personalized readiness score, your business
              tier, and a tailored next-step recommendation — plus a branded PDF report
              delivered to your inbox.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 tmg-fade-up-d3">
              {step === STEP_INTRO ? (
                <button
                  data-testid="readiness-start-btn"
                  onClick={() => { trackEvent("ai_readiness_started"); setStep(STEP_CHECKLIST); }}
                  className="tmg-btn-primary"
                >
                  Start the Checklist <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <a href="#assessment" className="tmg-btn-secondary">
                  Continue Where You Left Off
                </a>
              )}
              <Link to="/book-call" className="tmg-btn-secondary">
                Skip — Book a Call
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs text-[var(--tmg-muted)] font-mono-sm uppercase tracking-[0.18em]">
              <span className="flex items-center gap-2"><Check className="w-3 h-3 text-[var(--tmg-gold)]" /> 16 questions</span>
              <span className="flex items-center gap-2"><Check className="w-3 h-3 text-[var(--tmg-gold)]" /> Personalized PDF</span>
              <span className="flex items-center gap-2"><Check className="w-3 h-3 text-[var(--tmg-gold)]" /> No fluff</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative tmg-fade-up-d2 hidden lg:block">
            <PreviewCard />
          </div>
        </div>
      </section>

      {/* ASSESSMENT */}
      <section id="assessment" className="tmg-section">
        <div className="tmg-container max-w-4xl">
          {step !== STEP_INTRO && step !== STEP_RESULT && (
            <div className="mb-10">
              <div className="flex items-end justify-between mb-3">
                <p className="tmg-overline">
                  {step === STEP_CHECKLIST
                    ? `Section ${sectionIdx + 1} of ${sections.length || "—"}`
                    : "— One Last Step"}
                </p>
                <p className="font-mono-sm text-xs text-[var(--tmg-muted)]">
                  {answeredCount} / {totalQuestions}
                </p>
              </div>
              <Progress value={progressPct} className="h-1 bg-[var(--tmg-line)]" />
            </div>
          )}

          {step === STEP_INTRO && (
            <IntroBlock onStart={() => { trackEvent("ai_readiness_started"); setStep(STEP_CHECKLIST); }} sections={sections} />
          )}

          {step === STEP_CHECKLIST && currentSection && (
            <div data-testid="checklist-step" className="bg-white border border-[var(--tmg-line)] p-8 sm:p-12 tmg-fade-up">
              <p className="tmg-overline-gold mb-4">— {currentSection.title}</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-10">
                Answer honestly — there are no wrong answers.
              </h2>

              <div className="space-y-4">
                {currentSection.questions.map((q, i) => (
                  <div
                    key={q.id}
                    data-testid={`question-${q.id}`}
                    className="border border-[var(--tmg-line)] p-6 hover:border-[var(--tmg-gold)]/40 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-6 flex-col sm:flex-row">
                      <div className="flex items-start gap-4 flex-1">
                        <span className="font-mono-sm text-xs text-[var(--tmg-gold)] mt-1">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <p className="text-base sm:text-lg leading-snug">{q.text}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <AnswerBtn
                          active={answers[q.id] === true}
                          onClick={() => toggle(q.id, true)}
                          testId={`answer-${q.id}-yes`}
                        >
                          Yes
                        </AnswerBtn>
                        <AnswerBtn
                          active={answers[q.id] === false}
                          onClick={() => toggle(q.id, false)}
                          testId={`answer-${q.id}-no`}
                        >
                          Not Yet
                        </AnswerBtn>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mt-10 pt-6 border-t border-[var(--tmg-line)]">
                <button
                  onClick={() => sectionIdx === 0 ? setStep(STEP_INTRO) : setSectionIdx((i) => i - 1)}
                  className="text-sm font-semibold text-[var(--tmg-muted)] hover:text-[var(--tmg-obsidian)] flex items-center gap-2"
                  data-testid="checklist-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={() => isLastSection ? setStep(STEP_EMAIL) : setSectionIdx((i) => i + 1)}
                  disabled={!sectionComplete}
                  data-testid="checklist-next-btn"
                  className="tmg-btn-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  {isLastSection ? "Get My Report" : "Next Section"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === STEP_EMAIL && (
            <form
              onSubmit={submit}
              data-testid="lead-email-form"
              className="bg-white border border-[var(--tmg-line)] p-8 sm:p-12 tmg-fade-up"
            >
              <p className="tmg-overline-gold mb-4">— Almost There</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-6">
                Where should we send your report?
              </h2>
              <p className="text-[var(--tmg-muted)] leading-relaxed mb-8 max-w-xl">
                We'll generate your personalized PDF instantly — no waiting, no spam.
                You'll also get the on-screen results immediately.
              </p>

              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <Label className="tmg-overline">First name *</Label>
                  <Input
                    data-testid="lead-name-input"
                    required
                    value={lead.name}
                    onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="tmg-overline">Email *</Label>
                  <Input
                    data-testid="lead-email-input"
                    type="email"
                    required
                    value={lead.email}
                    onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2 mb-8">
                <Label className="tmg-overline">Business name (optional)</Label>
                <Input
                  data-testid="lead-business-input"
                  value={lead.business_name}
                  onChange={(e) => setLead((l) => ({ ...l, business_name: e.target.value }))}
                />
              </div>

              <div className="flex items-center justify-between pt-6 border-t border-[var(--tmg-line)]">
                <button
                  type="button"
                  onClick={() => setStep(STEP_CHECKLIST)}
                  className="text-sm font-semibold text-[var(--tmg-muted)] hover:text-[var(--tmg-obsidian)] flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="lead-submit-btn"
                  className="tmg-btn-primary disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                  ) : (
                    <>Show My Results <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>

              <p className="mt-6 text-xs text-[var(--tmg-muted)]">
                By submitting, you'll receive your report and occasional emails from TMG
                Consulting Group. Unsubscribe anytime.
              </p>
            </form>
          )}

          {step === STEP_RESULT && result && (
            <ResultBlock
              result={result}
              pdfUrl={pdfUrl}
              name={lead.name}
              onRetake={() => {
                setAnswers({});
                setSectionIdx(0);
                setStep(STEP_INTRO);
                setResult(null);
              }}
            />
          )}
        </div>
      </section>

      {step !== STEP_RESULT && <FinalCTA />}
    </>
  );
}

function AnswerBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-6 py-3 text-sm uppercase tracking-[0.14em] font-semibold border transition-all duration-200 ${
        active
          ? "bg-[var(--tmg-obsidian)] text-[var(--tmg-gold)] border-[var(--tmg-obsidian)]"
          : "bg-white text-[var(--tmg-obsidian)] border-[var(--tmg-line)] hover:border-[var(--tmg-obsidian)]"
      }`}
    >
      {children}
    </button>
  );
}

function PreviewCard() {
  return (
    <div className="relative">
      <div aria-hidden className="absolute -inset-3 border border-[var(--tmg-gold)]/40" />
      <div className="relative bg-[var(--tmg-obsidian)] text-white p-8 sm:p-10">
        <p className="tmg-overline-gold mb-2">— Sample Report Preview</p>
        <p className="font-mono-sm text-xs text-white/50 mb-6">PERSONALIZED · INSTANT · PDF</p>
        <div className="bg-white/[0.04] border border-white/10 p-6">
          <div className="flex items-end gap-4 mb-5">
            <div className="font-display text-6xl font-semibold text-[var(--tmg-gold)] leading-none">
              72%
            </div>
            <div className="pb-2">
              <p className="tmg-overline-gold mb-1">— Your Tier</p>
              <p className="font-display text-xl font-semibold">Build Ready</p>
            </div>
          </div>
          <div className="h-px bg-white/10 my-5" />
          <div className="space-y-2 text-xs text-white/70">
            {[
              ["AI Awareness", "3 / 3"],
              ["Workflow Clarity", "3 / 4"],
              ["Tools & Stack", "2 / 3"],
              ["Automation Readiness", "3 / 3"],
              ["Strategic Foundation", "2 / 3"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <span>{k}</span>
                <span className="text-[var(--tmg-gold)] font-mono-sm">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-6 text-xs text-white/50 leading-relaxed">
          Your real report will include your custom score, breakdown by area, and a
          tailored next-step recommendation.
        </p>
      </div>
    </div>
  );
}

function IntroBlock({ onStart, sections }) {
  return (
    <div className="grid lg:grid-cols-12 gap-12 tmg-fade-up">
      <div className="lg:col-span-7">
        <p className="tmg-overline mb-5">— What You'll Get</p>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-8">
          A clear picture of where your business actually stands.
        </h2>
        <ul className="space-y-5 mb-10">
          {[
            ["Your Readiness Score (0–100%)", "See exactly how prepared your business is for AI and automation."],
            ["Your Readiness Tier", "Foundation Builder, Systems Ready, or Build Ready — based on your real answers."],
            ["A Tailored Next Step", "We'll recommend the right service for where you are, not where someone else is."],
            ["A Branded PDF Report", "Instant download. Yours to keep, share, or revisit anytime."],
          ].map(([t, d]) => (
            <li key={t} className="flex gap-4">
              <span className="w-8 h-8 bg-[var(--tmg-obsidian)] text-[var(--tmg-gold)] flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4" />
              </span>
              <div>
                <p className="font-display text-lg font-semibold leading-snug">{t}</p>
                <p className="text-sm text-[var(--tmg-muted)] mt-1">{d}</p>
              </div>
            </li>
          ))}
        </ul>
        <button onClick={onStart} className="tmg-btn-primary" data-testid="intro-start-btn">
          Begin — It Takes 3 Minutes <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <div className="lg:col-span-5">
        <div className="bg-[var(--tmg-ivory-2)] p-8">
          <p className="tmg-overline-gold mb-6">— What We'll Ask About</p>
          <ol className="space-y-4">
            {sections.map((s, i) => (
              <li key={s.id} className="flex items-baseline gap-4 border-b border-[var(--tmg-line)] pb-3 last:border-0 last:pb-0">
                <span className="font-mono-sm text-xs text-[var(--tmg-gold)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-base font-medium flex-1">{s.title}</span>
                <span className="font-mono-sm text-xs text-[var(--tmg-muted)]">{s.questions.length}q</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

function ResultBlock({ result, pdfUrl, name, onRetake }) {
  const { user } = useAuth();
  const tierColor =
    result.percent <= 40 ? "Foundation" : result.percent <= 70 ? "Systems" : "Build";

  return (
    <div data-testid="result-block" className="tmg-fade-up">
      <div className="bg-[var(--tmg-obsidian)] text-white p-10 sm:p-14 relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 90% 10%, rgba(212,175,55,0.25), transparent 50%)",
          }}
        />
        <div className="relative">
          <p className="tmg-overline-gold mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> YOUR REPORT IS READY · {tierColor.toUpperCase()}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-2">
            {name?.split(" ")[0] || "Hey"}, here's where you stand.
          </h2>
          <p className="text-white/70 max-w-xl mb-10">
            We've also generated your branded PDF report — download it below or save the link
            for later.
          </p>

          <div className="grid sm:grid-cols-3 gap-px bg-white/10 mb-10">
            <div className="bg-[var(--tmg-obsidian)] p-8">
              <p className="tmg-overline-gold mb-3">— Score</p>
              <div className="font-display text-6xl font-semibold text-[var(--tmg-gold)] leading-none">
                {result.percent}%
              </div>
              <p className="text-xs text-white/60 mt-2 font-mono-sm">
                {result.yes_count} of {result.total} indicators
              </p>
            </div>
            <div className="bg-[var(--tmg-obsidian)] p-8">
              <p className="tmg-overline-gold mb-3">— Your Tier</p>
              <div className="font-display text-3xl font-semibold leading-tight">
                {result.tier}
              </div>
            </div>
            <div className="bg-[var(--tmg-obsidian)] p-8">
              <p className="tmg-overline-gold mb-3">— Best Next Step</p>
              <div className="font-display text-xl font-semibold leading-tight text-[var(--tmg-gold)]">
                {result.recommendation}
              </div>
            </div>
          </div>

          <p className="text-base sm:text-lg text-white/85 leading-relaxed max-w-3xl">
            {result.summary}
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="result-download-pdf"
              onClick={() => trackEvent("ai_pdf_downloaded")}
              className="tmg-btn-primary"
            >
              Download PDF Report <Download className="w-4 h-4" />
            </a>
            <Link to="/book-call" data-testid="result-book-call-btn" className="bg-transparent border border-[var(--tmg-gold)] text-[var(--tmg-gold)] hover:bg-[var(--tmg-gold)] hover:text-[var(--tmg-obsidian)] font-semibold px-7 py-4 text-sm uppercase tracking-[0.14em] transition-colors inline-flex items-center gap-2">
              Book a Discovery Call <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Save to account banner */}
      {!user && (
        <div className="bg-[var(--tmg-ivory-2)] border-x border-b border-[var(--tmg-line)] p-6 sm:p-8 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <p className="tmg-overline-gold mb-2">— Save Your Report</p>
            <h3 className="font-display text-lg font-semibold leading-tight">
              Sign in to save this report and revisit it anytime.
            </h3>
            <p className="text-sm text-[var(--tmg-muted)] mt-1">
              Free · Google sign-in · Access your history in your account.
            </p>
          </div>
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            onClick={() => beginGoogleAuth("/my-account")}
            data-testid="ai-save-account-btn"
            className="tmg-btn-dark"
          >
            Sign in with Google
          </button>
        </div>
      )}

      {/* Section breakdown */}
      <div className="bg-white border border-t-0 border-[var(--tmg-line)] p-10 sm:p-14">
        <p className="tmg-overline mb-5">— Your Breakdown</p>
        <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-tight mb-8">
          How you scored across each area.
        </h3>
        <div className="space-y-4">
          {Object.entries(result.section_scores).map(([key, s]) => {
            const pct = Math.round((s.yes / s.total) * 100);
            return (
              <div key={key}>
                <div className="flex justify-between items-baseline mb-1.5">
                  <span className="font-display text-base font-medium capitalize">
                    {key.replace(/_/g, " ")}
                  </span>
                  <span className="font-mono-sm text-xs text-[var(--tmg-muted)]">
                    {s.yes} / {s.total}
                  </span>
                </div>
                <div className="h-1.5 bg-[var(--tmg-line)] overflow-hidden">
                  <div
                    className="h-full bg-[var(--tmg-gold)] transition-all duration-1000"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 pt-8 border-t border-[var(--tmg-line)] flex flex-wrap gap-4 justify-between items-center">
          <button onClick={onRetake} className="text-sm font-semibold text-[var(--tmg-muted)] hover:text-[var(--tmg-obsidian)] underline-offset-4 hover:underline">
            Retake the assessment
          </button>
          <Link to="/offerings" className="tmg-btn-secondary">
            Explore Offerings <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
